import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
};

const inputCls =
  "w-full rounded-xl border border-[#E5DEC3] bg-white px-4 py-3.5 text-sm text-[#121A16] placeholder-[#4A5A52]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0F382A] focus:shadow-[0_0_0_3px_rgba(15,56,42,0.08)]";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ds_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <p className="font-serif text-3xl font-medium text-[#0F382A]">
            Don Sparkle<span className="text-[#D4AF37]">.</span>
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#4A5A52]">
            Admin Access
          </p>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="rounded-[1.75rem] border border-[#E5DEC3] bg-white p-8 shadow-[0_40px_80px_-40px_rgba(11,29,22,0.18)]"
        >
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A5A52]" htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@donsparklecleaning.co.uk"
            className={inputCls}
          />
          <label className="mb-2 mt-5 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A5A52]" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-[#0F382A] py-3.5 text-sm font-semibold text-[#FAF8F5] transition-[background-color,opacity] duration-300 hover:bg-[#1B523F] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-[#4A5A52]">
          <a href="/" className="underline decoration-[#D4AF37] underline-offset-4 hover:text-[#0F382A]">
            Back to website
          </a>
        </p>
      </motion.div>
    </div>
  );
}
